import React, { useState, useEffect } from "react";
import InviteUserModal from "./InviteUserModal";

/**
 * Shared user dropdown component with consistent formatting and sorting.
 * Displays users as "fullName (email)" sorted by fullName, then email.
 *
 * Props:
 *   value            – selected user ID (string), may be "invite:<id>" for invited users
 *   onChange         – (value: string) => void
 *   users            – array of user objects
 *   loading          – boolean; shows disabled state while loading
 *   placeholder      – string; select placeholder text
 *   className        – extra CSS classes for the <select>
 *   disabled         – boolean
 *   onInviteSuccess  – (inviteData) => void (optional); when provided, a "+" button is shown
 *                      that opens the InviteUserModal. The invited user is added to the
 *                      dropdown with an "invite:" prefix and auto-selected. The actual
 *                      assignment is deferred to when the parent form is saved.
 *   canInvite        – boolean (default true); when false the "+" invite button is hidden even if
 *                      onInviteSuccess is provided. Use to mirror the backend invite permission so
 *                      users who can't invite (e.g. self-scope contributors) don't hit a dead-end.
 *   nonMemberIds     – optional Set of user IDs to tag with "(not a member)". Used by the assign
 *                      modals on private deals so owners/admins see which assignees would need to
 *                      be granted deal access first.
 */
const UserSelect = ({
    value,
    onChange,
    users = [],
    loading = false,
    placeholder = "-- Select a User --",
    className = "",
    disabled = false,
    onInviteSuccess,
    canInvite = true,
    nonMemberIds,
}) => {
    const [showInvite, setShowInvite] = useState(false);
    const [inviteSent, setInviteSent] = useState(false);
    const [inviteEmailDisabled, setInviteEmailDisabled] = useState(false);
    const [invitedUsers, setInvitedUsers] = useState([]);

    // Remove invited users that now exist in the real users list (accepted invite)
    useEffect(() => {
        setInvitedUsers(prev =>
            prev.filter(inv => !users.some(u => u.email === inv.email))
        );
    }, [users]);

    // Combine real users with invited users
    const allUsers = [...users, ...invitedUsers];

    // Sort users by fullName (or full_name), then email
    const sortedUsers = [...allUsers].sort((a, b) => {
        const nameA = (a.fullName || a.full_name || "").toLowerCase();
        const nameB = (b.fullName || b.full_name || "").toLowerCase();
        if (nameA !== nameB) return nameA.localeCompare(nameB);
        return (a.email || "").localeCompare(b.email || "");
    });

    // Format display: "fullName (email)" with "(Invited)" suffix for invited users
    // and "(not a member)" for users outside a private deal (see nonMemberIds).
    const formatUser = (user) => {
        const name = user.fullName || user.full_name;
        let label = name ? `${name} (${user.email})` : user.email;
        if (String(user.id).startsWith("invite:")) label = `${label} (Invited)`;
        else if (nonMemberIds && nonMemberIds.has(user.id)) label = `${label} (not a member)`;
        return label;
    };

    return (
        <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
            <select
                value={String(value || "")}
                onChange={(e) => onChange(e.target.value)}
                className={`flex-1 min-w-0 border border-border rounded-md px-3 py-2 bg-input-bg text-content-primary focus:outline-none focus:ring-2 focus:ring-accent-cyan/50 focus:border-accent-cyan ${className}`}
                disabled={loading || disabled}
            >
                <option value="">{placeholder}</option>
                {sortedUsers.map((u) => (
                    <option key={u.id} value={String(u.id)}>
                        {formatUser(u)}
                    </option>
                ))}
            </select>

            {onInviteSuccess && canInvite && (
                <>
                    <button
                        type="button"
                        onClick={() => setShowInvite(true)}
                        className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-md border border-blue-300 bg-blue-100 text-blue-600 hover:bg-blue-200 hover:border-blue-400 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-300"
                        title="Invite new user"
                    >
                        <span className="text-xl font-light leading-none">+</span>
                    </button>

                    <InviteUserModal
                        isOpen={showInvite}
                        onClose={() => setShowInvite(false)}
                        assignableOnly={true}
                        onSuccess={(inviteData) => {
                            // With email delivery disabled the modal fires onSuccess only when the user
                            // closes it, so closing here never hides an invite link they have not seen.
                            setShowInvite(false);
                            setInviteSent(true);
                            setInviteEmailDisabled(inviteData?.emailDelivery === 'disabled');
                            // Add invited user as a synthetic entry and auto-select
                            if (inviteData?.userInviteId) {
                                const syntheticId = `invite:${inviteData.userInviteId}`;
                                setInvitedUsers((prev) => [
                                    ...prev,
                                    {
                                        id: syntheticId,
                                        fullName: inviteData.fullName,
                                        email: inviteData.email,
                                    },
                                ]);
                                onChange(syntheticId);
                            }
                            onInviteSuccess?.(inviteData);
                        }}
                    />
                </>
            )}
        </div>
        {inviteSent && (
            <p className="text-sm text-blue-600">
                {inviteEmailDisabled ? 'Invitation created.' : 'Invitation sent!'} Click Save to complete the assignment. It will take effect once the user accepts the invitation.
            </p>
        )}
        </div>
    );
};

export default UserSelect;
